import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import PageHeader from '@/components/PageHeader'
import { Mail, Phone, Calendar, Search } from 'lucide-react'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import UserAvatar from '@/components/UserAvatar'

const Prospects = () => {
  const { user } = useAuthStore()
  const isSuperAdmin = user?.role?.id === 1

  const [prospects, setProspects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    let isActive = true

    const fetchProspects = async () => {
      setLoading(true)
      setError('')
      try {
        const companyUrl = apiService.getCompanyUrl()
        const endpoint = isSuperAdmin
          ? apiService.withCompanyEndpoint('/companyadmin/getcompanyprospects', companyUrl)
          : apiService.withCompanyEndpoint('/getconsultantprospects', companyUrl)
        const response = await apiService.post(
          endpoint,
          {
            fromIndex: 0,
            limit: 50,
            orderBy: 'DESC',
            orderByOn: 'id',
            searchtext: ''
          },
          controller.signal
        )

        const rows = response?.data?.rows || response?.data?.data?.rows || []

        if (!isActive || controller.signal.aborted) return

        if (response.success && Array.isArray(rows)) {
          setProspects(rows)
          setError('')
        } else {
          setError(response?.error || 'Unable to load prospects')
        }
      } catch (err) {
        if (!isActive || controller.signal.aborted) return
        setError(err?.message || 'Unable to load prospects')
      } finally {
        if (!isActive || controller.signal.aborted) return
        setLoading(false)
      }
    }

    fetchProspects()

    return () => {
      isActive = false
      controller.abort()
    }
  }, [isSuperAdmin])

  const filteredProspects = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return prospects
    return prospects.filter((prospect) => {
      const name = `${prospect.firstname || ''} ${prospect.lastname || ''}`.toLowerCase()
      const email = prospect.email?.toLowerCase() || ''
      const phone = prospect.personal?.mobilephone?.toLowerCase() || ''
      const city = prospect.personal?.city?.toLowerCase() || ''
      return name.includes(term) || email.includes(term) || phone.includes(term) || city.includes(term)
    })
  }, [prospects, search])

  const formatDate = (value) => {
    if (!value) return 'N/A'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  }

  const getFullName = (first, last) => {
    const name = `${first || ''} ${last || ''}`.trim()
    return name || 'Unknown Prospect'
  }

  return (
    <div className="app-page space-y-6">
      <PageHeader title="Prospects" subtitle="Manage potential clients with real data from the platform">
        <div className="relative w-full sm:w-80">
          <label htmlFor="prospect-search" className="sr-only">Search prospects</label>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            id="prospect-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, or city"
            className="h-10 pl-9"
          />
        </div>
      </PageHeader>

      <Card className="app-shell">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Prospect List</span>
            <span className="text-sm font-normal text-slate-500">
              {filteredProspects.length} of {prospects.length || 0}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`prospect-loading-${index}`}
                  className="rounded-2xl border border-slate-200/80 bg-white p-4"
                >
                  <div className="animate-pulse grid grid-cols-1 gap-3 md:[grid-template-columns:minmax(0,2.2fr)_minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)] md:items-center">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-slate-200" />
                      <div className="space-y-2 w-full">
                        <div className="h-3 w-2/3 rounded bg-slate-200" />
                        <div className="h-3 w-1/3 rounded bg-slate-200" />
                      </div>
                    </div>
                    <div className="h-3 w-full rounded bg-slate-200" />
                    <div className="h-3 w-full rounded bg-slate-200" />
                    <div className="h-3 w-full rounded bg-slate-200" />
                    <div className="h-3 w-full rounded bg-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && error && prospects.length === 0 && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          {!loading && !error && filteredProspects.length === 0 && (
            <p className="text-sm text-slate-500">No prospects found.</p>
          )}

          {!loading && !error && filteredProspects.length > 0 && (
            <div className="space-y-3">
              <div className="hidden rounded-2xl border border-slate-200/80 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid md:[grid-template-columns:minmax(0,2.2fr)_minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)] md:gap-3">
                <span>Prospect</span>
                <span>Email</span>
                <span>Phone</span>
                <span>Location</span>
                <span>Added</span>
              </div>

              {filteredProspects.map((prospect) => {
                const fullName = getFullName(prospect.firstname, prospect.lastname)
                const locationParts = [prospect.personal?.city, prospect.personal?.state]
                  .filter(Boolean)
                  .join(', ')

                return (
                  <div
                    key={prospect.id}
                    className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 transition-colors hover:bg-slate-50/70 md:[grid-template-columns:minmax(0,2.2fr)_minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)] md:items-center"
                  >
                    <div className="flex items-center gap-4">
                      <UserAvatar
                        name={fullName}
                        email={prospect.email}
                        className="w-12 h-12"
                      />
                      <div className="min-w-0">
                        <h4 className="truncate text-base font-semibold text-slate-900">{fullName}</h4>
                        <p className="text-xs text-slate-500">ID: {prospect.id || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:hidden">Email</p>
                      <p className="flex items-center gap-1 text-sm text-slate-700">
                        <Mail className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        <span className="truncate">{prospect.email || 'N/A'}</span>
                      </p>
                    </div>

                    <div>
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:hidden">Phone</p>
                      <p className="flex items-center gap-1 text-sm text-slate-700">
                        <Phone className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        <span>{prospect.personal?.mobilephone || 'N/A'}</span>
                      </p>
                    </div>

                    <div>
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:hidden">Location</p>
                      <p className="truncate text-sm text-slate-700">{locationParts || 'N/A'}</p>
                    </div>

                    <div>
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:hidden">Added</p>
                      <p className="flex items-center gap-1 text-sm text-slate-700">
                        <Calendar className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        <span>{formatDate(prospect.created_at)}</span>
                      </p>
                    </div>

                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default Prospects
